import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import api from '../../utils/api';

const BlogAnalytics = () => {
    const { id } = useParams();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [blogData, setBlogData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                // Fetch the blog summary
                const blogRes = await api.get(`/api/blogs/my/${id}`);
                setBlogData(blogRes.data);

                // Fetch analytics
                const res = await api.get(`/api/blogs/my/${id}/analytics`);
                setAnalyticsData(res.data);
            } catch (err) {
                console.error("Analytics fetch error:", err);
                setError(err.response?.data?.error || "Failed to load analytics");
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchAnalytics();
    }, [id]);

    const chartData = useMemo(() => {
        if (!analyticsData?.views_over_time) return [];
        // Map data to standard recharts format and ensure it's chronologically sorted
        return analyticsData.views_over_time.map(item => ({
            date: new Date(item.view_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
            views: parseInt(item.views_count, 10),
        }));
    }, [analyticsData]);

    const refererData = useMemo(() => {
        if (!analyticsData?.top_referers) return [];
        return analyticsData.top_referers.map(r => ({
            source: r.source || 'Direct',
            views: parseInt(r.views_count, 10)
        }));
    }, [analyticsData]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <NavigationHeader />
                <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
                    <Icon name="Loader2" size={32} className="animate-spin text-emerald-500" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <NavigationHeader />
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-red-200">
                        <Icon name="AlertCircle" size={40} className="text-red-500 mx-auto mb-4" />
                        <h2 className="text-lg font-bold text-gray-900 mb-2">Error Loading Analytics</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <Link to="/submit" className="text-emerald-600 hover:text-emerald-700 font-boldtracking-wider text-xs">
                            ← Back to Submissions
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const totalViews = chartData.reduce((sum, day) => sum + day.views, 0);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <NavigationHeader />
            <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-24">
                
                <div className="mb-8">
                    <Link to="/submit?tab=my-blogs" className="text-sm font-boldtracking-wider text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-2 mb-4">
                        <Icon name="ArrowLeft" size={14} /> Back to My Blogs
                    </Link>
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-1 leading-tight">Analytics Overview</h1>
                            <p className="text-slate-500 text-sm font-medium">{blogData?.title}</p>
                        </div>
                        <div className="flex items-center gap-4 bg-white px-5 py-3 rounded-xl shadow-sm border border-slate-200 shrink-0">
                            <div>
                                <p className="text-[10px] font-boldtracking-widest text-slate-400 mb-0.5">Total Views</p>
                                <p className="text-2xl font-black text-emerald-600 leading-none">{totalViews.toLocaleString()}</p>
                            </div>
                            <div className="h-10 w-px bg-slate-200" />
                            <div>
                                <p className="text-[10px] font-boldtracking-widest text-slate-400 mb-0.5">Likes</p>
                                <p className="text-2xl font-black text-slate-700 leading-none">{blogData?.likes_count || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* View Chart */}
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                        <h2 className="text-[11px] font-boldtracking-widest text-slate-500 mb-6 flex items-center gap-2">
                            <Icon name="TrendingUp" size={14} className="text-emerald-500" /> Views Over Time (Last 30 Days)
                        </h2>
                        {chartData.length > 0 ? (
                            <div className="h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10E58F" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#10E58F" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} dy={10} />
                                        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                                        <Tooltip 
                                            contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                                            itemStyle={{ color: '#0f172a', fontWeight: 'bold' }}
                                            cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                                        />
                                        <Area type="monotone" dataKey="views" name="Views" stroke="#10E58F" strokeWidth={3} fillOpacity={1} fill="url(#colorViews)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-72 flex flex-col items-center justify-center text-slate-400">
                                <Icon name="BarChart2" size={32} className="mb-2 opacity-50" />
                                <p className="text-sm font-medium">No views recorded yet.</p>
                            </div>
                        )}
                    </div>

                    {/* Referers */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col">
                        <h2 className="text-[11px] font-boldtracking-widest text-slate-500 mb-6 flex items-center gap-2">
                            <Icon name="Globe" size={14} className="text-indigo-500" /> Top Traffic Sources
                        </h2>
                        {refererData.length > 0 ? (
                            <div className="flex-1">
                                <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                                    <BarChart data={refererData} layout="vertical" margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                                        <XAxis type="number" hide />
                                        <YAxis dataKey="source" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#475569', fontWeight: 600 }} width={80} />
                                        <Tooltip 
                                            cursor={{ fill: '#f8fafc' }}
                                            contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                        />
                                        <Bar dataKey="views" name="Views" fill="#818cf8" radius={[0, 4, 4, 0]} maxBarSize={40} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                                <Icon name="MousePointerClick" size={32} className="mb-2 opacity-50" />
                                <p className="text-sm font-medium">No traffic data.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default BlogAnalytics;
